import type { ImageMeta } from '@didto/domain';
import { uuidv7 } from '@didto/domain';
import type { Row } from './db';
import { env } from './env';
import { supabase } from './supabase';

/**
 * Catalog reads (RLS-guarded selects) and the image upload helper. Writes go through commands.
 * A café catalog is small, so each list is fetched whole and joined in memory.
 */
export type Category = Pick<
  Row<'category'>,
  'id' | 'name' | 'color' | 'image_path' | 'sort' | 'archived_at'
>;
export type Product = Pick<
  Row<'product'>,
  | 'id'
  | 'name'
  | 'category_id'
  | 'description'
  | 'production_route_id'
  | 'color'
  | 'shape'
  | 'image_path'
  | 'image_meta'
  | 'track_stock'
  | 'sort'
  | 'archived_at'
>;
export type Variant = Pick<
  Row<'product_variant'>,
  | 'id'
  | 'product_id'
  | 'name'
  | 'sku'
  | 'barcode'
  | 'price_minor'
  | 'is_default'
  | 'sort'
  | 'archived_at'
>;
export type VariantCost = Pick<Row<'product_variant_cost'>, 'product_variant_id' | 'cost_minor'>;
export type ModifierGroup = Pick<
  Row<'modifier_group'>,
  'id' | 'name' | 'min_select' | 'max_select' | 'sort' | 'archived_at'
>;
export type ModifierOption = Pick<
  Row<'modifier_option'>,
  'id' | 'modifier_group_id' | 'name' | 'price_delta_minor' | 'sort' | 'archived_at'
>;
export type ProductModifierGroup = Pick<
  Row<'product_modifier_group'>,
  'product_id' | 'modifier_group_id' | 'sort'
>;
export type Availability = Pick<
  Row<'product_availability'>,
  'location_id' | 'product_variant_id' | 'is_available' | 'reason'
>;
export type RouteRef = Pick<Row<'production_route'>, 'id' | 'code' | 'name' | 'color'>;

/** Query keys: every catalog key starts with 'catalog' so one invalidation refreshes all of them. */
export const catalogKeys = {
  all: ['catalog'] as const,
  categories: ['catalog', 'categories'] as const,
  products: ['catalog', 'products'] as const,
  variants: ['catalog', 'variants'] as const,
  costs: ['catalog', 'costs'] as const,
  groups: ['catalog', 'modifier-groups'] as const,
  options: ['catalog', 'modifier-options'] as const,
  assignments: ['catalog', 'assignments'] as const,
  availability: ['catalog', 'availability'] as const,
  routes: ['routes'] as const,
};

async function rows<T>(q: PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export const fetchCategories = () =>
  rows<Category>(
    supabase
      .from('category')
      .select('id, name, color, image_path, sort, archived_at')
      .order('sort'),
  );
export const fetchProducts = () =>
  rows<Product>(
    supabase
      .from('product')
      .select(
        'id, name, category_id, description, production_route_id, color, shape, image_path, image_meta, track_stock, sort, archived_at',
      )
      .order('sort'),
  );
export const fetchVariants = () =>
  rows<Variant>(
    supabase
      .from('product_variant')
      .select('id, product_id, name, sku, barcode, price_minor, is_default, sort, archived_at')
      .order('sort'),
  );
/** Empty for anyone without catalog.manage (row security), which is the intended behaviour. */
export const fetchCosts = () =>
  rows<VariantCost>(supabase.from('product_variant_cost').select('product_variant_id, cost_minor'));
export const fetchModifierGroups = () =>
  rows<ModifierGroup>(
    supabase
      .from('modifier_group')
      .select('id, name, min_select, max_select, sort, archived_at')
      .order('sort'),
  );
export const fetchModifierOptions = () =>
  rows<ModifierOption>(
    supabase
      .from('modifier_option')
      .select('id, modifier_group_id, name, price_delta_minor, sort, archived_at')
      .order('sort'),
  );
export const fetchAssignments = () =>
  rows<ProductModifierGroup>(
    supabase
      .from('product_modifier_group')
      .select('product_id, modifier_group_id, sort')
      .order('sort'),
  );
export const fetchAvailability = () =>
  rows<Availability>(
    supabase
      .from('product_availability')
      .select('location_id, product_variant_id, is_available, reason'),
  );
export const fetchRoutes = () =>
  rows<RouteRef>(
    supabase
      .from('production_route')
      .select('id, code, name, color')
      .is('archived_at', null)
      .order('sort'),
  );

/** Public URL of an uploaded catalog image (the bucket is public for reads). */
export function catalogImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  return `${env.supabaseUrl}/storage/v1/object/public/catalog-images/${path}`;
}

export const IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** Upload into the business folder; the caller records the returned path through a command. */
export async function uploadCatalogImage(
  businessId: string,
  file: File,
): Promise<{ path: string; meta: ImageMeta }> {
  // `invalid_payload:` so the sentence reaches staff as it is (lib/errors.ts)
  if (!(IMAGE_MIME as readonly string[]).includes(file.type))
    throw new Error('invalid_payload: Use a JPEG, PNG or WebP image.');
  if (file.size > IMAGE_MAX_BYTES)
    throw new Error('invalid_payload: Images must be 5 MB or smaller.');
  const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
  const path = `${businessId}/${uuidv7()}.${ext}`;
  const { error } = await supabase.storage
    .from('catalog-images')
    .upload(path, file, { contentType: file.type, cacheControl: '31536000', upsert: false });
  if (error) throw error;
  const dims = await readImageSize(file).catch(() => null);
  return { path, meta: { mime: file.type, size: file.size, ...(dims ?? {}) } };
}

function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unreadable image'));
    };
    img.src = url;
  });
}
